import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

/**
 * PageTransition — a short crossfade + rise on route change, shared by all three SPAs.
 *
 * docs/06 §7 confines the ACCENT libraries (magicui / smoothui / unlumen) to the landing page and
 * bars *decorative* animation from work screens. A route-change crossfade is not decoration — it
 * is a navigation micro-interaction that tells the operator "this is a new screen, not a stale one
 * that failed to update". framer-motion is already a dependency of every SPA, so this adds no new
 * engine to the bundle. The deviation is recorded in docs/06 §7.
 *
 * The component is ROUTER-AGNOSTIC on purpose: it takes the transition boundary as `routeKey`
 * rather than reaching for `useLocation`, so the same code works under `<BrowserRouter>` (admin,
 * client) and a data router (provider), and could in principle be driven by any key at all.
 *
 * NO `AnimatePresence mode="wait"`. It was the crossfade engine here until it became the exact bug
 * it was meant to prevent: navigating between pages faster than the 0.18 s exit animation strands
 * framer-motion's presence bookkeeping (a key that re-enters while still exiting, e.g. A→B→A), and
 * the incoming screen never mounts — the workspace goes BLANK until the next navigation nudges it.
 * "A new screen, not a stale one that failed to update" is precisely what broke.
 *
 * The screens are opaque and occupy the same slot, so there is nothing to "wait" for: we don't
 * need the outgoing screen to finish leaving before the new one mounts, only to not visibly
 * overlap. Re-keying a single `motion.div` on `routeKey` does exactly that — React unmounts the
 * old subtree and mounts the new one atomically, and the enter animation replays each time. No
 * exit animation, no presence state to deadlock, no way to end up with an empty container.
 *
 * Reduced motion is honored in JS, not merely CSS: framer-motion drives a transform the global
 * `prefers-reduced-motion` CSS clamp cannot reach, so under that preference we render the child
 * statically — an instant, snap-free swap.
 */
export function PageTransition({
  routeKey,
  children,
  className,
}: {
  /** Changes exactly when the route changes — typically `location.pathname`. */
  routeKey: string;
  children: ReactNode;
  className?: string;
}) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      key={routeKey}
      className={className}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}
